import { Buffer, BufferUsage, Geometry, Mesh, Shader, type UniformGroup } from 'pixi.js';
import { MAX_MAGNETS, PHYSICS, VISUALS, WORLD_HEIGHT, WORLD_WIDTH } from '../config.ts';
import { FIELD_GLSL, type MagneticField } from '../simulation/MagneticField.ts';
import type { ViewRect } from './Renderer.ts';

/**
 * The table the game is played on, seen from directly above.
 *
 * Two things are drawn on it: a magnet's reach, as a soft glow whose radius comes from
 * the same force law the simulation uses, and — while planning — the potential landscape
 * as equipotential contours.
 *
 * The contours are the game's main instrument. Fluid runs downhill on this surface, from
 * blue towards red, perpendicular to the lines, and the whole puzzle is arranging magnets
 * so that the hill runs from the source to the goal. They are drawn *statically*: an
 * earlier version animated them, which made them crawl and moire and fight the fluid for
 * attention. Here they hold still, they fade out the moment the run starts, and they are
 * gone entirely once there is nothing left to plan.
 *
 * Everything else is surface: a machined slate top, a fine grid for scale, a warm pool of
 * light in the middle of the board, and a vignette.
 */

const vertex = /* glsl */ `#version 300 es
in vec2 aPosition;

uniform mat3 uProjectionMatrix;
uniform mat3 uWorldTransformMatrix;
uniform mat3 uTransformMatrix;

out vec2 vWorld;

void main() {
    mat3 mvp = uProjectionMatrix * uWorldTransformMatrix * uTransformMatrix;
    gl_Position = vec4((mvp * vec3(aPosition, 1.0)).xy, 0.0, 1.0);
    vWorld = aPosition;
}
`;

const fragment = /* glsl */ `#version 300 es
precision highp float;

in vec2 vWorld;
out vec4 fragColor;

uniform vec4  uMagnets[${MAX_MAGNETS}];
uniform float uMagnetCount;
uniform vec3  uFieldParams;
uniform float uTime;
uniform vec2  uWorldSize;
uniform float uContour;
uniform float uContourDensity;
uniform float uFieldGain;
/** Contour gain that survives the run, once uContour has eased to zero. */
uniform float uRunLines;
/** The arena's bounding box in world units, padded: x0, y0, x1, y1. */
uniform vec4 uArena;

${FIELD_GLSL}

/**
 * The potential, and how much field there is here regardless of sign.
 *
 * The second number is what tells a genuine zero crossing — the fold between a positive
 * and a negative magnet, which is worth drawing — apart from the far corners of the
 * board, where the potential is also nearly zero simply because nothing reaches them.
 */
vec2 potentialAndMagnitude(vec2 p) {
    float potential = 0.0;
    float magnitude = 0.0;
    int count = int(uMagnetCount);
    for (int i = 0; i < MAX_MAGNETS; i++) {
        if (i >= count) break;
        vec4 magnet = uMagnets[i];
        float distance = max(length(magnet.xy - p), uFieldParams.z);
        float contribution = magnet.z * POTENTIAL_SCALE / distance;
        potential += contribution;
        magnitude += abs(contribution);
    }
    return vec2(potential, magnitude);
}

/** Distance to the closest magnet, for masking the clamped plateau at its centre. */
float nearestMagnetDistance(vec2 p) {
    float nearest = 1e9;
    int count = int(uMagnetCount);
    for (int i = 0; i < MAX_MAGNETS; i++) {
        if (i >= count) break;
        nearest = min(nearest, length(uMagnets[i].xy - p));
    }
    return nearest;
}

// The slate the whole board sits on.
//
// Solved backwards through the post chain rather than picked: black point, ACES, the
// contrast curve and the cool shadow tint together land this near L 0.08 on screen, which
// is high enough to read as a surface and low enough that the ferrofluid — whose body
// resolves to about L 0.005 — still has sixteen to one of contrast against it.
const vec3 TABLE      = vec3(0.090, 0.100, 0.132);
const vec3 GRID       = vec3(0.20, 0.24, 0.33);
/** The attract/repel fold: cooler than the equipotentials, so hue tells them apart. */
const vec3 ZERO_LINE  = vec3(0.72, 0.86, 0.92);
const vec3 POSITIVE   = vec3(0.52, 0.10, 0.17);
const vec3 NEGATIVE   = vec3(0.07, 0.22, 0.55);
const vec3 POSITIVE_HI = vec3(0.74, 0.19, 0.25);
const vec3 NEGATIVE_HI = vec3(0.20, 0.48, 0.88);
const vec3 LINE       = vec3(0.88, 0.91, 0.98);

/** Cheap value noise, used only to break up banding in the large gradients. */
float hash(vec2 p) {
    return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453);
}

/** Anti-aliased grid lines at the given spacing. */
float grid(vec2 p, float spacing, float thickness) {
    vec2 cell = abs(fract(p / spacing) - 0.5) * spacing;
    vec2 width = fwidth(p) * 0.5 + 0.0001;
    vec2 line = smoothstep(thickness + width, thickness - width, cell);
    return max(line.x, line.y);
}

void main() {
    vec2 p = vWorld;
    vec2 uv = p / uWorldSize;
    vec2 centered = (uv - 0.5) * vec2(1.0, uWorldSize.y / uWorldSize.x);

    vec2 field = potentialAndMagnitude(p);
    float potential = field.x;
    float magnitude = field.y;

    // --- the potential surface ------------------------------------------
    //
    // The board *is* the field. Everything the player does is arrange this surface so it
    // runs downhill from the source to the goal, so it gets the full frame rather than a
    // tint over a table: saturated red where fluid is pulled, blue where it is pushed.
    float signedStrength = potential / (0.55 + abs(potential));
    float strength = abs(signedStrength);

    vec3 base = potential >= 0.0 ? POSITIVE : NEGATIVE;
    vec3 bright = potential >= 0.0 ? POSITIVE_HI : NEGATIVE_HI;
    vec3 tint = mix(base, bright, smoothstep(0.68, 1.0, strength));

    // Colour holds through the run at reduced strength: the field is still worth reading
    // while it plays out, it just stops being the loudest thing on screen.
    float wash = mix(0.62, 1.0, uContour);

    // Added over the table, not mixed towards it.
    //
    // Mixing replaced the table entirely once the field got strong, so a board with a
    // magnet on it had no dark values left anywhere in frame — every wall shadow, every
    // black the fluid is made of, sat on a mid-crimson slab. Adding keeps the slate
    // underneath and caps how bright the field can ever get, which leaves saturation and
    // contour density to carry field strength instead of brightness.
    // Held down outside the walls.
    //
    // The field is real everywhere — it passes through walls, and a magnet dropped in the
    // margin is a legal move — so this attenuates rather than clips. But at full strength
    // out here the densest, most saturated region of the entire frame sat *below the
    // bottom wall*, where no fluid can ever go. The arena stopped being a table with an
    // edge and became a scribble on a glowing sheet, and reading the field inside the
    // walls meant mentally masking half the image.
    vec2 playEdge = min(p - uArena.xy, uArena.zw - p);
    float insidePlay = smoothstep(-26.0, 26.0, min(playEdge.x, playEdge.y));

    float drive = clamp(strength * 1.6, 0.0, 1.0) * wash * mix(0.34, 1.0, insidePlay);
    vec3 color = TABLE + tint * drive * uFieldGain;

    // The machined surface underneath.
    //
    // No longer multiplied out where the field is strong. Light landing on a table cannot
    // remove scribe lines from it, and erasing them declared the grid to be part of the
    // field visualisation rather than part of the table — which stops the table reading as
    // an object at all, and makes every shadow cast onto it a shadow cast onto a picture.
    // The field is added, not mixed, so the grid simply loses relative contrast where the
    // wash is bright, which is what a real engraved line does under a coloured light.
    color += GRID * grid(p, 50.0, 0.9) * 0.10;
    color += GRID * grid(p, 250.0, 1.6) * 0.14;

    // --- contours ---------------------------------------------------------
    // Drawn in both phases. The run used to gate this off entirely and drop the wash to a
    // third, which together made the running board statistically identical to the empty
    // one — the player committed to a rich field diagram and got back the blank table they
    // started from, at the exact moment the field was doing all the work. It dims now
    // instead of disappearing, and the lines stay where they were: a contour family that
    // moves reads as a bug, one that fades reads as the same instrument turned down.
    if (uContour > 0.002 || uRunLines > 0.0) {
        // True equipotentials: the level set of the same potential the fluid falls down.
        // Only *which* levels get drawn is a choice, and drawing a lot of them is what
        // turns a colour wash into something you can read a gradient off — lines crowd
        // where the surface is steep, which is exactly where it matters.
        float phase = potential * uContourDensity;
        float ridge = abs(fract(phase) - 0.5) * 2.0;

        // Derivative-based width keeps a line a pixel wide however fast the potential is
        // changing, and past the point where the contours pack tighter than the screen
        // can show them they fade out altogether. Letting them wash together instead
        // leaves a smear of moire right where the field is most interesting.
        float crowding = fwidth(phase);
        float width = clamp(crowding * 2.0, 0.02, 0.6);
        float line = smoothstep(1.0 - width, 1.0, ridge);
        line *= 1.0 - smoothstep(0.18, 0.5, crowding);

        // Gate on there actually being a field here.
        //
        // With no magnets the potential is zero everywhere, so fract(0) puts every pixel
        // exactly on the zeroth contour and fwidth(0) collapses the width to its lower
        // clamp — the whole board lights up as one continuous line. That is what turned
        // the empty planning view, the first thing anyone sees, into a pale grey slab
        // roughly six times brighter than the near-black table it is supposed to be.
        float present = smoothstep(0.02, 0.12, magnitude);
        line *= present;

        // Inside the clamp radius the potential is flat by construction, so there is
        // nothing there to contour.
        float plateau = smoothstep(uFieldParams.z * 0.9, uFieldParams.z * 1.1, nearestMagnetDistance(p));
        // Dimmed outside the walls for the same reason the wash is: half the instrument
        // was drawn at full ink where the fluid can never reach.
        line *= mix(0.40, 1.0, insidePlay);
        color += LINE * line * plateau * (uContour * 0.25 + uRunLines * 0.08);

        // The fold between attracting and repelling. Fluid on one side of this line goes
        // one way and on the other side the other way, which makes it the single most
        // useful curve on the board.
        // Told apart by hue and by being a touch brighter than its neighbours, not by
        // being blown out. At 0.85 in the same white as the equipotentials it measured
        // L 0.75 — brighter than the wall tops, brighter than anything but the fluid's
        // specular, and the only thing on the board over the bloom threshold, so it also
        // glowed. Edge to edge across the frame and two pixels wide, it read as a display
        // fault rather than as the most useful curve here. It also had no antialiasing
        // headroom, so it crawled whenever a magnet moved.
        float zeroWidth = fwidth(potential) * 2.6 + 1e-5;
        float zero = 1.0 - smoothstep(0.0, zeroWidth, abs(potential));
        zero *= smoothstep(0.25, 0.9, magnitude);
        // Gain measured on screen rather than reasoned about. The tonemap lifts hard down
        // here — 0.45 still came out at L 0.67 — and the number that matters is
        // bloomThreshold 0.52: above it the fold does not merely look bright, it emits,
        // which is what made a 2px band across the whole frame read as a display fault.
        // This lands it level with the ordinary equipotentials at about L 0.50, and lets
        // hue and continuity say which line it is instead of brightness. Dropping it
        // further inverts the hierarchy the other way: the most useful curve on the board
        // would become the dimmest.
        color += ZERO_LINE * zero * (uContour * 0.19 + uRunLines * 0.06);
    }

    // --- frame ------------------------------------------------------------
    vec2 edge = min(p, uWorldSize - p);
    float nearest = min(edge.x, edge.y);
    float inside = step(0.0, nearest);
    color *= mix(0.30, 1.0, inside);
    color += vec3(0.24, 0.30, 0.44) * (1.0 - smoothstep(0.0, 3.0, abs(nearest))) * 0.5;

    color *= 1.0 - 0.20 * dot(centered, centered);
    color += (hash(p * 0.7 + uTime) - 0.5) * 0.006;

    fragColor = vec4(color, 1.0);
}
`;

export class BackgroundRenderer {
  readonly mesh: Mesh<Geometry, Shader>;

  private readonly positionBuffer: Buffer;
  private readonly uniforms: UniformGroup;

  constructor() {
    // A quad, rewritten on resize to cover whatever slice of world space is visible.
    this.positionBuffer = new Buffer({
      data: new Float32Array([0, 0, WORLD_WIDTH, 0, WORLD_WIDTH, WORLD_HEIGHT, 0, WORLD_HEIGHT]),
      usage: BufferUsage.VERTEX | BufferUsage.COPY_DST,
    });

    const geometry = new Geometry({
      attributes: {
        aPosition: { buffer: this.positionBuffer, format: 'float32x2', stride: 8, offset: 0 },
      },
      indexBuffer: new Uint32Array([0, 1, 2, 0, 2, 3]),
    });

    const shader = Shader.from({
      gl: { vertex, fragment },
      resources: {
        backgroundUniforms: {
          uMagnets: { value: new Float32Array(MAX_MAGNETS * 4), type: 'vec4<f32>', size: MAX_MAGNETS },
          uMagnetCount: { value: 0, type: 'f32' },
          uFieldParams: {
            value: new Float32Array([
              PHYSICS.magneticStrength,
              PHYSICS.magneticFalloff,
              PHYSICS.magneticMinDistance,
            ]),
            type: 'vec3<f32>',
          },
          uTime: { value: 0, type: 'f32' },
          uWorldSize: { value: new Float32Array([WORLD_WIDTH, WORLD_HEIGHT]), type: 'vec2<f32>' },
          uContour: { value: 0, type: 'f32' },
          uContourDensity: { value: VISUALS.fieldContourDensity, type: 'f32' },
          uFieldGain: { value: VISUALS.fieldWashGain, type: 'f32' },
          uRunLines: { value: 0, type: 'f32' },
          uArena: {
            value: new Float32Array([0, 0, WORLD_WIDTH, WORLD_HEIGHT]),
            type: 'vec4<f32>',
          },
        },
      },
    });

    this.uniforms = shader.resources.backgroundUniforms as UniformGroup;
    this.mesh = new Mesh({ geometry, shader, label: 'background' });
  }

  /**
   * The rectangle the player can actually reach, in world units.
   *
   * The field is attenuated outside it rather than clipped: a magnet dropped in the margin
   * is a legal move, and its near field has to stay readable at the moment it is placed.
   */
  setArena(x0: number, y0: number, x1: number, y1: number): void {
    const arena = this.uniforms.uniforms.uArena as Float32Array;
    arena[0] = x0;
    arena[1] = y0;
    arena[2] = x1;
    arena[3] = y1;
  }

  /** Stretch the quad over the visible world rect, overscan included. */
  setView(view: ViewRect): void {
    const data = this.positionBuffer.data as Float32Array;
    data[0] = view.x0;
    data[1] = view.y0;
    data[2] = view.x1;
    data[3] = view.y0;
    data[4] = view.x1;
    data[5] = view.y1;
    data[6] = view.x0;
    data[7] = view.y1;
    this.positionBuffer.update();
  }

  /** `contour` is 1 while planning and eases to 0 once the run is committed. */
  update(field: MagneticField, elapsed: number, contour: number): void {
    const uniforms = this.uniforms.uniforms;
    (uniforms.uMagnets as Float32Array).set(field.pack());
    uniforms.uMagnetCount = field.count;
    uniforms.uTime = elapsed;
    uniforms.uContour = contour;
    // The complement, so the two gains cross-fade rather than summing: planning gets the
    // full-strength instrument, the run gets a quiet reference copy of the same lines.
    uniforms.uRunLines = 1 - contour;
    uniforms.uContourDensity = VISUALS.fieldContourDensity;
    uniforms.uFieldGain = VISUALS.fieldWashGain;

    // Physics constants are hot-tunable, so re-push them rather than caching.
    const params = uniforms.uFieldParams as Float32Array;
    params[0] = PHYSICS.magneticStrength;
    params[1] = PHYSICS.magneticFalloff;
    params[2] = PHYSICS.magneticMinDistance;
  }
}
